<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  formatBrazilPhone,
  extractBrazilPhoneE164,
} from 'dashboard/composables/useBrazilPhoneMask';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import KanbansAPI from 'dashboard/api/kanbans';

const props = defineProps({
  kanbanId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSubmitting = ref(false);

const stages = useMapGetter('kanbans/getStagesByKanbanId');
const currentStages = computed(() => stages.value(props.kanbanId));

// Phone-capable channels only — a card needs a phone number to reach a contact_inbox.
const PHONE_CHANNEL_TYPES = [
  INBOX_TYPES.EVOLUTION,
  INBOX_TYPES.WHATSAPP,
  INBOX_TYPES.TWILIO,
  INBOX_TYPES.SMS,
  INBOX_TYPES.API,
];
const allInboxes = useMapGetter('inboxes/getInboxes');
const phoneCapableInboxes = computed(() =>
  allInboxes.value.filter(inbox =>
    PHONE_CHANNEL_TYPES.includes(inbox.channel_type)
  )
);

const defaultState = () => ({
  name: '',
  phoneDisplay: '',
  inboxId: '',
  stageId: '',
});

const state = reactive(defaultState());
const touched = reactive({ name: false, phone: false, inbox: false, stage: false });

const phoneE164 = computed(() => extractBrazilPhoneE164(state.phoneDisplay));

const nameError = computed(() =>
  touched.name && !state.name.trim()
    ? t('KANBAN.CREATE_CARD.ERRORS.NAME_REQUIRED')
    : ''
);
const phoneError = computed(() =>
  touched.phone && !phoneE164.value
    ? t('KANBAN.CREATE_CARD.ERRORS.PHONE_INVALID')
    : ''
);
const inboxError = computed(() =>
  touched.inbox && !state.inboxId
    ? t('KANBAN.CREATE_CARD.ERRORS.INBOX_REQUIRED')
    : ''
);
const stageError = computed(() =>
  touched.stage && !state.stageId
    ? t('KANBAN.CREATE_CARD.ERRORS.STAGE_REQUIRED')
    : ''
);

const isFormInvalid = computed(
  () =>
    !state.name.trim() || !phoneE164.value || !state.inboxId || !state.stageId
);

const onPhoneInput = event => {
  state.phoneDisplay = formatBrazilPhone(event.target.value);
};

const open = (preselectedStageId = null) => {
  Object.assign(state, defaultState());
  Object.assign(touched, {
    name: false,
    phone: false,
    inbox: false,
    stage: false,
  });

  if (preselectedStageId) state.stageId = preselectedStageId;
  if (phoneCapableInboxes.value.length === 1) {
    state.inboxId = phoneCapableInboxes.value[0].id;
  }

  if (!allInboxes.value.length) {
    store.dispatch('inboxes/get');
  }

  dialogRef.value?.open();
};

watch(
  () => phoneCapableInboxes.value.length,
  count => {
    if (count === 1 && !state.inboxId) {
      state.inboxId = phoneCapableInboxes.value[0].id;
    }
  }
);

const handleConfirm = async () => {
  touched.name = true;
  touched.phone = true;
  touched.inbox = true;
  touched.stage = true;

  if (isFormInvalid.value) return;

  isSubmitting.value = true;
  try {
    const { data } = await KanbansAPI.createCard({
      kanbanId: props.kanbanId,
      name: state.name.trim(),
      phone_number: phoneE164.value,
      inbox_id: state.inboxId,
      kanban_stage_id: state.stageId,
    });
    useAlert(t('KANBAN.CREATE_CARD.SUCCESS'));
    emit('created', data);
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('KANBAN.CREATE_CARD.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KANBAN.CREATE_CARD.TITLE')"
    :confirm-button-label="t('KANBAN.CREATE_CARD.SUBMIT')"
    :cancel-button-label="t('KANBAN.CREATE_CARD.CANCEL')"
    :is-loading="isSubmitting"
    width="sm"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="t('KANBAN.CREATE_CARD.NAME_LABEL')"
        :placeholder="t('KANBAN.CREATE_CARD.NAME_PLACEHOLDER')"
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
        autofocus
        @blur="touched.name = true"
      />

      <Input
        :model-value="state.phoneDisplay"
        :label="t('KANBAN.CREATE_CARD.PHONE_LABEL')"
        :placeholder="t('KANBAN.CREATE_CARD.PHONE_PLACEHOLDER')"
        :message="phoneError"
        :message-type="phoneError ? 'error' : 'info'"
        @input="onPhoneInput"
        @blur="touched.phone = true"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-12">
          {{ t('KANBAN.CREATE_CARD.INBOX_LABEL') }}
        </label>
        <select
          v-model="state.inboxId"
          class="reply-composer--input"
          @blur="touched.inbox = true"
        >
          <option value="" disabled>
            {{ t('KANBAN.CREATE_CARD.INBOX_PLACEHOLDER') }}
          </option>
          <option
            v-for="inbox in phoneCapableInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
        <span v-if="inboxError" class="text-xs text-n-ruby-9">
          {{ inboxError }}
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-12">
          {{ t('KANBAN.CREATE_CARD.STAGE_LABEL') }}
        </label>
        <select
          v-model="state.stageId"
          class="reply-composer--input"
          @blur="touched.stage = true"
        >
          <option value="" disabled>
            {{ t('KANBAN.CREATE_CARD.STAGE_PLACEHOLDER') }}
          </option>
          <option v-for="stage in currentStages" :key="stage.id" :value="stage.id">
            {{ stage.name }}
          </option>
        </select>
        <span v-if="stageError" class="text-xs text-n-ruby-9">
          {{ stageError }}
        </span>
      </div>
    </div>
  </Dialog>
</template>
